import {RunHistoryRow, RunKeyInfo} from '../../types/run';
import {DataDogPanelType} from '../../util/panelTypes';
import {ChartAreaOption} from '../../util/plotHelpers/chartTypes';
import {SingleQuery} from '../../util/queryTypes';
import * as RunTypes from '../../util/runTypes';

type EMAAggregation = {
  type: 'ema';
  parameters: {
    alpha: number;
    zeroDebias?: boolean;
  };
};

type GaussianAggregation = {
  type: 'gaussian';
  parameters: {
    sigma: number;
  };
};

type TimeWeightedEMAAggregation = {
  type: 'time_weighted_ema';
  parameters: {
    alpha: number;
  };
};

type RunningAverageAggregation = {
  type: 'running_average';
  parameters: {
    windowSize: number;
  };
};

export type MetricTransformation =
  | EMAAggregation
  | GaussianAggregation
  | TimeWeightedEMAAggregation
  | RunningAverageAggregation;

export type HistorySpec = {
  keys: string[];
  samples: number;
  minStep?: number;
  maxStep?: number;
};

export type BucketedHistorySpec = {
  keys: string[];
  bins: number;
  samples?: number;
  xAxis?: string;
  minStep?: number;
  maxStep?: number;
  minX?: number;
  maxX?: number;
  groupArea?: ChartAreaOption;

  // key is the metric key, value is the preprocessing aggregations for that metric
  metricTransformations?: {[key: string]: MetricTransformation[]};
};

export type StepAxisBHS = BucketedHistorySpec & {
  xAxis: '_step';
  minX: never;
  maxX: never;
};
export type CustomAxisBHS = BucketedHistorySpec & {
  xAxis: Exclude<string, '_step'>;
  minX?: number;
  maxX?: number;
};

export function specIsStepAxis(spec: BucketedHistorySpec): spec is StepAxisBHS {
  return spec.xAxis === '_step';
}

// This is the user query interface. This has a lot of legacy stuff in it.
// We should switch to using the Query from runs-low when we can.
export interface Query {
  // level?: 'run' | 'subgroup' | 'group';
  aggregationKeys?: string[];
  bucketedHistorySpecs?: BucketedHistorySpec[];
  configKeys?: string[];
  disabled?: boolean;
  displayName?: string;
  enableArtifactCounts?: boolean;
  enableTags?: boolean;
  entityName: string;
  fullAggregations?: boolean;
  fullConfig?: boolean;
  fullSummary?: boolean;
  groupKeys?: string[];
  history?: boolean;
  historyFields?: string[];
  historyKeyInfo?: boolean;
  historySamples?: number;
  historySpecs?: HistorySpec[];
  internalProjectId?: string | undefined;
  keysLoading: boolean;
  page?: {size: number};
  panelType: DataDogPanelType;
  pollInterval?: number;
  projectName: string;
  queries: SingleQuery[];
  runName?: string;
  select?: string;
  summaryKeys?: string[];
  wandbKeys?: string[];
}

export type RunWithRunsetInfo = RunTypes.Run & {
  runsetInfo: {
    id: string;
    name?: string;
  };
};

export function isRunWithRunsetInfo(r: RunTypes.Run): r is RunWithRunsetInfo {
  return (
    'runsetInfo' in r &&
    typeof (r as any).runsetInfo?.id === 'string' &&
    ['string', 'undefined'].indexOf(typeof (r as any).runsetInfo?.name) !== -1
  );
}

// Data holds results.
export interface Data {
  initialLoading: boolean;
  loading: boolean;
  entityName: string;
  projectName: string;
  runName?: string;
  displayName?: string;
  filtered: RunWithRunsetInfo[];
  filteredRunsById: {[key: string]: RunWithRunsetInfo};
  keyInfo: RunKeyInfo;
  totalRuns: number;
  limit?: number;
  histories: {
    data: Array<{
      name: string;
      displayName: string;
      history: RunHistoryRow[];
    }>;
  };
  doRunSetGrouping?: boolean;
  loadMore?(page: number): void;
}
